import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { VisualizationData } from '../../types';
import { formatNumber } from '../../lib/utils';

interface BarChartProps {
  data: VisualizationData;
  animated?: boolean;
}

const DEFAULT_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
];

export function BarChart({ data, animated }: BarChartProps) {
  const { config, data: chartData } = data;
  const { title, xAxis, yAxis, colors = DEFAULT_COLORS } = config;

  const xField = xAxis || (chartData[0] ? Object.keys(chartData[0])[0] : 'name');
  const yField = yAxis || (chartData[0] ? Object.keys(chartData[0])[1] : 'value');

  return (
    <div className="w-full">
      {title && (
        <h3 className="mb-4 text-sm font-medium text-foreground">{title}</h3>
      )}
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              type="number"
              tickFormatter={(value) => formatNumber(value)}
              className="text-xs"
            />
            <YAxis
              type="category"
              dataKey={xField}
              width={120}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) =>
                typeof value === 'string' && value.length > 15
                  ? value.substring(0, 15) + '...'
                  : value
              }
            />
            <Tooltip
              formatter={(value) => {
                const numericValue =
                  typeof value === 'number' ? value : Number(value);
                const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
                return [
                  `${safeValue.toLocaleString()} (${formatNumber(safeValue)})`,
                  yField,
                ];
              }}
              labelFormatter={(label) => label}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
                padding: '8px 12px',
              }}
              labelStyle={{
                color: 'hsl(var(--foreground))',
                fontWeight: '600',
                marginBottom: '4px',
              }}
            />
            <Bar dataKey={yField} radius={[0, 4, 4, 0]} isAnimationActive={animated !== false}>
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
